<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import { ChampionshipRegistrationChangeAction } from "@/model/championship/championship-registration-change-action"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { ChampionshipRegistrationTeamChangeRequest } from "@/model/championship/championship-registration-team-change-request"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, $datetime, AnimateIf, useHttpClient, useInlineTable, useLoadingState } from "@/vf"
import { computed, ref, watch } from "vue"
import { useTaskViewMode } from "../../Issue/util"
import { useChampionshipRegistrationSelectTeamData } from "../championship-registration"
import { useRegistrationPolicy } from "../useRegistrationPolicy"

const props = defineProps<{
    changeRequest: ChampionshipRegistrationTeamChangeRequest
    team: ChampionshipRegistrationTeam
}>()

const http = useHttpClient()
const inlineTable = useInlineTable()
const viewMode = useTaskViewMode()
const registrationData = useChampionshipRegistrationSelectTeamData()
const policy = useRegistrationPolicy(
    computed(() => registrationData.value?.permissions),
    computed(() => viewMode),
)

// Separate category changes from member changes
const categoryChanges = computed(() => {
    if (!props.changeRequest.changes) return []
    return props.changeRequest.changes.filter(c => c.action === ChampionshipRegistrationChangeAction.ChangeCategory)
})

// Member and slot changes: adds first, then removes, then cancellations
const memberActionOrder = [
    ChampionshipRegistrationChangeAction.AddMember,
    ChampionshipRegistrationChangeAction.RemoveMember,
    ChampionshipRegistrationChangeAction.CancelMember,
    ChampionshipRegistrationChangeAction.CancelBlankSlot,
]

const memberChanges = computed(() =>
    (props.changeRequest.changes ?? [])
        .filter(c => memberActionOrder.includes(c.action!))
        .sort((a, b) => memberActionOrder.indexOf(a.action!) - memberActionOrder.indexOf(b.action!)),
)

type SummaryRole = "participant" | "alternate" | "entourages"

function toSummaryRole(role?: string): SummaryRole | null {
    if (role === "participant") return "participant"
    if (role === "alternate") return "alternate"
    if (["coach", "supervisor", "entourage", "entourages"].includes(role ?? "")) return "entourages"
    return null
}

/** How the changes move the named persons and the booked slots of a role. */
type RoleDelta = { named: number; slots: number }

const roleDelta = computed<Record<SummaryRole, RoleDelta>>(() => {
    const delta: Record<SummaryRole, RoleDelta> = {
        participant: { named: 0, slots: 0 },
        alternate: { named: 0, slots: 0 },
        entourages: { named: 0, slots: 0 },
    }

    for (const change of memberChanges.value) {
        const summaryRole = toSummaryRole(change.role)
        if (!summaryRole) continue

        switch (change.action) {
            case ChampionshipRegistrationChangeAction.AddMember:
                delta[summaryRole].named += 1
                delta[summaryRole].slots += 1
                break
            case ChampionshipRegistrationChangeAction.RemoveMember:
                delta[summaryRole].named -= 1
                delta[summaryRole].slots -= 1
                break
            // A canceled person gives their slot up, a canceled blank slot had no person
            case ChampionshipRegistrationChangeAction.CancelMember:
                delta[summaryRole].named -= 1
                delta[summaryRole].slots -= 1
                break
            case ChampionshipRegistrationChangeAction.CancelBlankSlot:
                delta[summaryRole].slots -= 1
                break
        }
    }

    return delta
})

const roleHasChanges = computed<Record<SummaryRole, boolean>>(() => {
    const changed: Record<SummaryRole, boolean> = {
        participant: false,
        alternate: false,
        entourages: false,
    }

    for (const change of memberChanges.value) {
        const summaryRole = toSummaryRole(change.role)
        if (summaryRole) changed[summaryRole] = true
    }

    return changed
})

const roleSummaries = computed(() => {
    const currentParticipants = props.team.numberOfParticipants ?? 0
    const currentAlternates = props.team.numberOfAlternates ?? 0
    const currentEntourages = props.team.numberOfEntourages ?? 0
    const namedParticipants = props.team.numberOfNamedParticipants ?? 0
    const namedAlternates = props.team.numberOfNamedAlternates ?? 0
    const namedEntourages = props.team.numberOfNamedEntourages ?? 0

    return [
        {
            key: "participant" as const,
            currentCount: currentParticipants,
            namedCount: namedParticipants,
            nextCount: currentParticipants + roleDelta.value.participant.slots,
            nextNamedCount: namedParticipants + roleDelta.value.participant.named,
            delta: roleDelta.value.participant.slots,
            hasChanges: roleHasChanges.value.participant,
        },
        {
            key: "alternate" as const,
            currentCount: currentAlternates,
            namedCount: namedAlternates,
            nextCount: currentAlternates + roleDelta.value.alternate.slots,
            nextNamedCount: namedAlternates + roleDelta.value.alternate.named,
            delta: roleDelta.value.alternate.slots,
            hasChanges: roleHasChanges.value.alternate,
        },
        {
            key: "entourages" as const,
            currentCount: currentEntourages,
            namedCount: namedEntourages,
            nextCount: currentEntourages + roleDelta.value.entourages.slots,
            nextNamedCount: namedEntourages + roleDelta.value.entourages.named,
            delta: roleDelta.value.entourages.slots,
            hasChanges: roleHasChanges.value.entourages,
        },
    ]
})

function deltaClass(delta: number) {
    if (delta > 0) return "text-success"
    if (delta < 0) return "text-danger"
    return "text-muted"
}

function trendIcon(delta: number) {
    if (delta > 0) return "fa-arrow-up"
    if (delta < 0) return "fa-arrow-down"
    return "fa-check"
}

function transSummaryRole(role: SummaryRole) {
    if (role === "participant") return trans("role.participant")
    if (role === "alternate") return trans("role.alternate")
    return trans("role.entourages")
}

const trans = defineTranslations({
    "role.participant": {
        de: "Teilnehmer",
        en: "Participant",
    },
    "role.alternate": {
        de: "Ersatzteilnehmer",
        en: "Alternate Participant",
    },
    "role.coach": {
        de: "Coach",
        en: "Coach",
    },
    "role.supervisor": {
        de: "Betreuer",
        en: "Supervisor",
    },
    "role.entourages": {
        de: "Entourage",
        en: "Entourages",
    },
    "decline": {
        de: "Ablehnen",
        en: "Decline",
    },
    "approve": {
        de: "Annehmen",
        en: "approve",
    },
    "approve_fees": {
        de: "Annehmen mit Preisaufschlag",
        en: "approve with additional fees",
    },

    "approve_confirmation": {
        de: "Möchten Sie die Änderungsanfrage wirklich annehmen?",
        en: "Do you really want to approve the change request?",
    },
    "approve_success": {
        de: "Die Änderungsanfrage wurde angenommen.",
        en: "The change request has been approved.",
    },

    "cancel": {
        de: "Stornieren",
        en: "Cancel",
    },
    "cancel_confirmation": {
        de: "Möchtest du die Änderungsanfrage wirklich stornieren?",
        en: "Do you really want to cancel the change request?",
    },
    "cancel_success": {
        de: "Die Änderungsanfrage wurde storniert.",
        en: "The change request has been cancelled.",
    },

    "decline_reason": {
        de: "Ablehnungsgrund",
        en: "Reason for decline",
    },
    "decline_submit": {
        de: "Ablehnen",
        en: "Decline",
    },

    "fee_quantity": {
        de: "Menge",
        en: "Quantity",
    },

    "fee_name": {
        de: "Bezeichnung",
        en: "Title",
    },
    "fee_price": {
        de: "Betrag",
        en: "Amount",
    },
    "add_fee": {
        de: "Gebühr hinzufügen",
        en: "Add Fee",
    },
    "submit_with_fees": {
        de: "Mit Gebühren bestätigen",
        en: "Submit with Fees",
    },

    "accepted_at": {
        de: "Diese Änderungsanfrage wurde am {date} angenommen.",
        en: "This change request was approved on {date}.",
    },
    "accepted_automatically_at": {
        de: "Diese Änderungsanfrage wurde am {date} automatisch angenommen.",
        en: "This change request was approved automatically on {date}.",
    },
    "cancelled_member": {
        de: "storniert",
        en: "cancelled",
    },
    "blank_slot": {
        de: "Freier Platz",
        en: "Blank slot",
    },
    "declined_at": {
        de: "Diese Änderungsanfrage wurde am {date} abgelehnt mit folgendem Grund:",
        en: "This change request was declined on {date} with the following reason:",
    },
    "cancelled_at": {
        de: "Diese Änderungsanfrage wurde vom Kunden am {date} storniert.",
        en: "This change request was cancelled by the customer on {date}.",
    },
    "category_change": {
        de: "Kategoriewechsel",
        en: "Category change",
    },
    "name": {
        de: "Name",
        en: "Name",
    },
    "birthDate": {
        de: "Geburtsdatum",
        en: "Date of birth",
    },
    "gender": {
        de: "Geschlecht",
        en: "Gender",
    },
    "role": {
        de: "Rolle",
        en: "Role",
    },
})

function transRole(role: string) {
    switch (role) {
        case "participant":
            return trans("role.participant")
        case "alternate":
            return trans("role.alternate")
        case "coach":
            return trans("role.coach")
        case "supervisor":
            return trans("role.supervisor")
        case "entourages":
            return trans("role.entourages")
    }
}

/*─────────────────────────────────────┐
│  Office actions                      │
└─────────────────────────────────────*/
type AdditionalCost = { name: string; price: number | undefined; quantity: number; description: string }
const additionalCosts = ref<AdditionalCost[]>([])
const submitting = useLoadingState()
const feeSuggestionLoaded = ref(false)

/**
 * Prefill the fee table with the fees configured on the championship for changes after the
 * registration deadline. The staff can adjust them before approving.
 */
async function loadFeeSuggestion() {
    if (feeSuggestionLoaded.value) {
        return
    }
    feeSuggestionLoaded.value = true

    const response = await http.get<{
        additionalCosts: { name: string; description: string; price: number; quantity: number }[]
    }>(`/championship/registration/change-request/${props.changeRequest.id}/fee-suggestion?viewMode=${viewMode}`)

    if (additionalCosts.value.length === 0) {
        additionalCosts.value = response.data.additionalCosts.map(cost => ({
            name: cost.name,
            description: cost.description,
            price: cost.price / 100,
            quantity: cost.quantity,
        }))
    }
}

function addAdditionalCost() {
    additionalCosts.value.push({ name: "", price: undefined, quantity: 1, description: "" })
}
function deleteAdditionalCost(cost: AdditionalCost) {
    additionalCosts.value = additionalCosts.value.filter(f => f !== cost)
}
function submit() {
    submitting.while(async () => {
        await http.post(
            `/championship/registration/change-request/${props.changeRequest.id}/approve?viewMode=${viewMode}`,
            {
                additionalCosts: additionalCosts.value.map(cost => ({
                    name: cost.name,
                    description: cost.description,
                    price: Math.round(+(cost.price ?? 0) * 100),
                    quantity: +cost.quantity,
                })),
            },
        )
        inlineTable?.close({ saved: true })
    })
}

const approveConfirm = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/change-request/${props.changeRequest.id}/approve?viewMode=${viewMode}`,
        )
    },
    afterAction() {
        inlineTable?.close({ saved: true })
    },
})
const approveFeesConfirm = createInlineConfirm(() => {})
watch(approveFeesConfirm.isOpen, open => {
    if (open) {
        loadFeeSuggestion()
    }
})
const declineConfirm = createInlineConfirm(() => {})

const declineReason = ref("")
async function decline() {
    await http.post(
        `/championship/registration/change-request/${props.changeRequest.id}/decline?viewMode=${viewMode}`,
        {
            reason: declineReason.value,
        },
    )
    inlineTable?.close({ saved: true })
}

/*─────────────────────────────────────┐
│  Customer actions                    │
└─────────────────────────────────────*/
const cancelConfirm = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/change-request/${props.changeRequest.id}/cancel?viewMode=${viewMode}`,
        )
    },
    afterAction() {
        inlineTable?.close({ saved: true })
    },
})
</script>
<template>
    <div class="alert alert-success" v-if="changeRequest.state === 'Approved'">
        <i class="fa fa-fw fa-check-circle text-success"></i>
        {{
            trans(changeRequest.isAutoAccepted ? "accepted_automatically_at" : "accepted_at", {
                date: $datetime(changeRequest.processedAt),
            })
        }}
    </div>
    <div class="alert alert-danger" v-if="changeRequest.state === 'Declined'">
        <i class="fa fa-fw fa-times-circle text-danger"></i>
        {{ trans("declined_at", { date: $datetime(changeRequest.processedAt) }) }}
        <div class="pl-5 pt-3 pre-line">{{ changeRequest.declineReason }}</div>
    </div>
    <div class="alert alert-warning" v-if="changeRequest.state === 'Cancelled'">
        <i class="far fa-fw fa-times-circle text-danger"></i>
        {{ trans("cancelled_at", { date: $datetime(changeRequest.processedAt) }) }}
    </div>

    <!-- Category changes - displayed prominently -->
    <div v-for="change of categoryChanges" :key="change.id" class="alert alert-primary d-flex align-items-center mb-3">
        <i class="fa fa-fw fa-exchange-alt fa-lg mr-3"></i>
        <div>
            <strong>{{ trans("category_change") }}</strong>
            <div class="mt-1">
                <s>{{ change.oldCategory?.name }}</s>
                <i class="fa fa-arrow-right mx-2"></i>
                <strong>{{ change.newCategory?.name }}</strong>
            </div>
        </div>
    </div>

    <div v-if="changeRequest.state === 'Requested' && memberChanges.length > 0" class="member-overview mb-3">
        <div class="member-overview__list">
            <div
                v-for="summary in roleSummaries"
                :key="summary.key"
                class="member-overview__item"
                :class="summary.delta === 0 ? 'member-overview__item--stable' : 'member-overview__item--changed'"
            >
                <div class="member-overview__content">
                    <div class="member-overview__label">
                        {{ transSummaryRole(summary.key) }}
                    </div>

                    <div class="member-overview__value font-weight-bold">
                        <span>{{ summary.namedCount }}/{{ summary.currentCount }}</span>
                        <template v-if="summary.hasChanges">
                            <i class="fa fa-fw fa-arrow-right text-muted mx-1"></i>
                            <span :class="deltaClass(summary.delta)">
                                {{ summary.nextNamedCount }}/{{ summary.nextCount }}
                            </span>
                        </template>
                    </div>
                </div>

                <div class="member-overview__trend" :class="summary.delta === 0 ? 'text-success' : 'text-danger'">
                    <i :class="['fa fa-fw', trendIcon(summary.delta)]"></i>
                </div>
            </div>
        </div>
    </div>

    <!-- Member changes - displayed in table -->
    <table v-if="memberChanges.length > 0" class="table-reset table table-borderless table-striped">
        <thead class="bg-white">
            <tr>
                <th></th>
                <th>{{ trans("name") }}</th>
                <th>{{ trans("role") }}</th>
                <th>{{ trans("birthDate") }}</th>
                <th>{{ trans("gender") }}</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="change in memberChanges" :key="change.id">
                <td>
                    <i
                        v-if="change.action === ChampionshipRegistrationChangeAction.AddMember"
                        class="fa fa-fw fa-plus-circle text-success"
                    ></i>
                    <i
                        v-else-if="change.action === ChampionshipRegistrationChangeAction.RemoveMember"
                        class="fa fa-fw fa-minus-circle text-danger"
                    ></i>
                    <i v-else class="fa fa-fw fa-ban text-danger" :title="trans('cancelled_member')"></i>
                </td>
                <td class="text-nowrap">
                    <template v-if="change.action === ChampionshipRegistrationChangeAction.CancelBlankSlot">
                        <span class="text-muted">{{ trans("blank_slot") }}</span>
                        <span class="text-danger ml-1">({{ trans("cancelled_member") }})</span>
                    </template>
                    <template v-else>
                        {{ change.memberFirstName }}
                        {{ change.memberLastName }}
                        <span
                            v-if="change.action === ChampionshipRegistrationChangeAction.CancelMember"
                            class="text-danger ml-1"
                        >
                            ({{ trans("cancelled_member") }})
                        </span>
                    </template>
                </td>
                <td>{{ transRole(change.role!) }}</td>
                <td>{{ change.memberBirthDate ? $date(change.memberBirthDate) : "" }}</td>
                <td>{{ change.memberGender ? $t("crud:gender." + change.memberGender) : "" }}</td>
            </tr>
        </tbody>
    </table>

    <div class="mt-5" v-if="changeRequest.state === 'Requested' && policy.ChangeRequestCancel">
        <VarsityInlineConfirmButton :controller="cancelConfirm">
            {{ trans("cancel") }}
        </VarsityInlineConfirmButton>
        <VarsityInlineConfirm :controller="cancelConfirm">
            <template #confirmation>{{ trans("cancel_confirmation") }}</template>
            <template #success>{{ trans("cancel_success") }}</template>
        </VarsityInlineConfirm>
    </div>

    <div
        class="d-flex align-items-end justify-content-between mt-5"
        v-if="changeRequest.state === 'Requested' && (policy.ChangeRequestApprove || policy.ChangeRequestDecline)"
    >
        <VarsityInlineConfirmGroup>
            <VarsityInlineConfirmButton :controller="approveConfirm">
                {{ trans("approve") }}
            </VarsityInlineConfirmButton>
            <VarsityInlineConfirmButton :controller="approveFeesConfirm">
                {{ trans("approve_fees") }}
            </VarsityInlineConfirmButton>
            <VarsityInlineConfirmButton :controller="declineConfirm">
                {{ trans("decline") }}
            </VarsityInlineConfirmButton>
        </VarsityInlineConfirmGroup>
    </div>

    <VarsityInlineConfirm :controller="approveConfirm">
        <template #confirmation>{{ trans("approve_confirmation") }}</template>
        <template #success>{{ trans("approve_success") }}</template>
    </VarsityInlineConfirm>
    <AnimateIf :if="approveFeesConfirm.isOpen.value">
        <table class="table">
            <thead>
                <tr>
                    <th>
                        {{ trans("fee_quantity") }}
                    </th>
                    <th>
                        {{ trans("fee_name") }}
                    </th>
                    <th>
                        {{ trans("fee_price") }}
                    </th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="additionalCosts.length === 0">
                    <td colspan="4" class="text-center py-5 text-muted">Keine zusätzlichen Gebühren hinzugefügt.</td>
                </tr>
                <tr v-for="(cost, index) in additionalCosts" :key="index">
                    <td>
                        <input
                            type="number"
                            class="form-control"
                            min="1"
                            step="1"
                            style="width: 10ex"
                            v-model="cost.quantity"
                        />
                    </td>
                    <td>
                        <input type="text" class="form-control" v-model="cost.name" v-focus placeholder="Titel" />
                        <input
                            type="text"
                            class="form-control mt-1"
                            v-model="cost.description"
                            placeholder="Beschreibung"
                        />
                    </td>
                    <td>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            class="form-control"
                            style="width: 20ex"
                            v-model="cost.price"
                        />
                    </td>
                    <td class="text-right">
                        <button class="btn btn-danger" type="button" @click="deleteAdditionalCost(cost)">
                            <i class="fa fa-fw fa-trash"></i>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
        <div class="d-flex justify-content-between align-items-center">
            <button type="button" class="btn btn-primary mt-3" @click="addAdditionalCost()">
                {{ trans("add_fee") }}
            </button>
            <LoadingButton :loading="submitting.state" class="btn btn-primary mt-3" @click="submit()">
                {{ trans("submit_with_fees") }}
            </LoadingButton>
        </div>
    </AnimateIf>

    <AnimateIf :if="declineConfirm.isOpen.value">
        <form @submit.prevent="decline()">
            <div>
                <b>{{ trans("decline_reason") }}</b>
                <textarea class="form-control" rows="5" v-model="declineReason" />
            </div>

            <div class="mt-3">
                <button class="btn btn-danger" type="submit">
                    {{ trans("decline_submit") }}
                </button>
            </div>
        </form>
    </AnimateIf>
</template>

<style scoped>
.member-overview__list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 1rem;
}

.member-overview__item {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    border-radius: 0.35rem;
    overflow: hidden;
}

.member-overview__content {
    flex: 1;
    padding: 0.5rem 0.7rem;
}

.member-overview__item--stable {
    background: #ddf3e5;
}

.member-overview__item--changed {
    background: #f8dfdf;
}

.member-overview__label {
    color: #24323d;
    font-size: 0.92rem;
    font-weight: 600;
    line-height: 1.1;
    margin-bottom: 0.12rem;
}

.member-overview__value {
    font-size: 1.5rem;
    line-height: 1.2;
}

.member-overview__trend {
    width: 2.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    margin-right: 0.5rem;
}
</style>
